
import React from 'react';
import { CreditCard, Shield, FileText, Bell, Share2, Globe, Clock, Users, Award, RefreshCw, Check, Star, Zap } from 'lucide-react';
import FadeIn from '@/components/animations/FadeIn';
import GlassCard from '@/components/ui-custom/GlassCard';

interface FeatureData {
  id: string;
  icon: React.ReactNode;
  title: string;
  description: string;
  benefits: string[];
  color: string;
}

const heroFeature = {
  id: 'identity',
  icon: <CreditCard className="w-6 h-6" />,
  title: "Digital Pet Identity & My Pet ID",
  description: "Give your pet a permanent digital identity with their own unique My Pet ID. This serves as their lifetime identification for instant verification, streamlined vet visits, and ownership proof.",
  benefits: [
    "Unique My Pet ID for permanent identification throughout their lifetime",
    "Instant verification and ownership proof anywhere in the world",
    "Streamlined vet check-ins and emergency identification"
  ],
  color: 'blue'
};

const supportingFeatures: FeatureData[] = [
  {
    id: 'documents',
    icon: <FileText className="w-5 h-5" />,
    title: "Secure Document Vault",
    description: "End the frustration of missing vaccination records. Store all your pet's essential documents securely with bank-level encryption.",
    benefits: [
      "Save 2-3 hours per vet visit with instant access",
      "Eliminate $50-200 fees for duplicate records"
    ],
    color: 'emerald'
  },
  {
    id: 'reminders',
    icon: <Bell className="w-5 h-5" />,
    title: "Smart Health Reminders",
    description: "Never miss critical pet care with intelligent reminders that can save you hundreds in emergency vet bills.",
    benefits: [
      "Prevent $500-2000 emergency vet bills",
      "Increase pet lifespan by up to 2 years"
    ],
    color: 'purple'
  },
  {
    id: 'sharing',
    icon: <Share2 className="w-5 h-5" />,
    title: "Instant Identity Sharing",
    description: "Generate secure, professional-grade reports instantly. No more frantic searches for documents during critical moments.",
    benefits: [
      "Save 30+ minutes per vet visit",
      "Enable faster, more accurate diagnoses"
    ],
    color: 'amber'
  },
  {
    id: 'verification',
    icon: <Shield className="w-5 h-5" />,
    title: "Identity Verification",
    description: "Prove pet ownership instantly with their My Pet ID. Perfect for vet visits, boarding, travel, and emergencies.",
    benefits: [
      "Instant ownership verification",
      "Faster emergency response"
    ],
    color: 'green'
  },
  {
    id: 'portability',
    icon: <Globe className="w-5 h-5" />,
    title: "Lifetime Portability",
    description: "Your pet's digital identity travels with them anywhere in the world. Never lose access to critical information.",
    benefits: [
      "Global accessibility from any device",
      "International recognition for travel"
    ],
    color: 'cyan'
  }
];

const FeatureDetailSection: React.FC = () => {
  return (
    <section className="py-8 lg:py-12 bg-gradient-to-br from-slate-50 via-blue-50/50 to-indigo-50/60 relative overflow-hidden" aria-labelledby="feature-details-heading">
      {/* Modern background elements */}
      <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-gradient-to-br from-blue-100/40 to-indigo-100/30 rounded-full blur-3xl -translate-y-32 translate-x-32" />
      <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-gradient-to-br from-purple-100/30 to-pink-100/25 rounded-full blur-3xl translate-y-32 -translate-x-32" />
      
      <div className="container mx-auto px-4 max-w-7xl relative z-10">
        {/* Section Header */}
        <div className="text-center mb-10">
          <FadeIn>
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-600 text-white shadow-xl mb-6">
              <Star className="w-6 h-6" />
            </div>
            <h2 
              id="feature-details-heading"
              className="text-2xl lg:text-3xl xl:text-4xl font-bold text-gray-900 mb-4 leading-tight"
            >
              Complete Digital Pet Identity Platform
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Comprehensive digital identity management for pets with advanced features designed to simplify pet care and improve health outcomes.
            </p>
          </FadeIn>
        </div>

        {/* Hero Feature */}
        <FadeIn delay={200}>
          <div className="mb-12">
            <GlassCard className="p-6 lg:p-8 bg-gradient-to-br from-white via-blue-50/50 to-indigo-50/40 border border-blue-100/50 shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-1">
              <div className="grid lg:grid-cols-2 gap-6 lg:gap-8 items-center">
                <div>
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 text-white shadow-lg mb-4">
                    {heroFeature.icon}
                  </div>
                  <h3 className="text-xl lg:text-2xl font-bold text-gray-900 mb-3">
                    {heroFeature.title}
                  </h3>
                  <p className="text-base text-gray-700 mb-4 leading-relaxed">
                    {heroFeature.description}
                  </p>
                  <div className="space-y-2">
                    {heroFeature.benefits.map((benefit, index) => (
                      <div key={index} className="flex items-start">
                        <div className="flex-shrink-0 w-5 h-5 rounded-full bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center mt-0.5 mr-3 shadow-sm">
                          <Check className="w-3 h-3 text-white" strokeWidth={3} />
                        </div>
                        <span className="text-sm text-gray-700 leading-relaxed font-medium">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-200/30 to-indigo-200/30 rounded-2xl blur-lg transform rotate-2" />
                  <div className="relative bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                    <div className="text-center">
                      <div className="w-16 h-16 mx-auto bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center mb-4 shadow-lg">
                        <CreditCard className="w-8 h-8 text-white" />
                      </div>
                      <h4 className="text-lg font-bold text-gray-900 mb-2">My Pet ID</h4>
                      <p className="text-gray-600 text-sm mb-3">Unique Digital Identity</p>
                      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-2 rounded-lg font-bold text-base shadow-md">
                        #MPD-2024-XXXX
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </GlassCard>
          </div>
        </FadeIn>

        {/* Supporting Features Grid */}
        <div className="mb-10">
          <FadeIn delay={400}>
            <h3 className="text-xl lg:text-2xl font-bold text-center text-gray-900 mb-8">
              Complete Feature Suite
            </h3>
          </FadeIn>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
            {supportingFeatures.map((feature, index) => (
              <FadeIn key={feature.id} delay={500 + index * 100}>
                <GlassCard className="p-5 h-full bg-white/80 backdrop-blur-sm border border-gray-100/50 hover:shadow-lg hover:border-blue-200/50 transition-all duration-300 transform hover:-translate-y-1">
                  <div className={`inline-flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-br ${
                    feature.color === 'emerald' ? 'from-emerald-500 to-green-500' :
                    feature.color === 'purple' ? 'from-purple-500 to-violet-500' :
                    feature.color === 'amber' ? 'from-amber-500 to-orange-500' :
                    feature.color === 'green' ? 'from-green-500 to-emerald-500' :
                    'from-cyan-500 to-blue-500'
                  } text-white shadow-md mb-3`}>
                    {feature.icon}
                  </div>
                  <h4 className="text-lg font-bold text-gray-900 mb-2">
                    {feature.title}
                  </h4>
                  <p className="text-gray-600 mb-3 leading-relaxed text-sm">
                    {feature.description}
                  </p>
                  <div className="space-y-1.5">
                    {feature.benefits.map((benefit, benefitIndex) => (
                      <div key={benefitIndex} className="flex items-start">
                        <div className="flex-shrink-0 w-3.5 h-3.5 rounded-full bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center mt-0.5 mr-2">
                          <Check className="w-2 h-2 text-white" strokeWidth={3} />
                        </div>
                        <span className="text-xs text-gray-600 leading-relaxed">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </GlassCard>
              </FadeIn>
            ))}
          </div>
        </div>

        {/* Enhanced Benefits Summary */}
        <FadeIn delay={800}>
          <div className="bg-gradient-to-br from-gray-900 via-blue-900 to-indigo-900 rounded-2xl p-6 lg:p-8 text-white relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-indigo-600/20" />
            <div className="relative z-10">
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-yellow-400 to-orange-500 text-gray-900 shadow-lg mb-4">
                  <Zap className="w-6 h-6" />
                </div>
                <h3 className="text-xl lg:text-2xl font-bold mb-3">
                  Why Pet Owners Choose <span className="text-blue-300">MyPetID</span>
                </h3>
                <p className="text-blue-100 text-base max-w-2xl mx-auto">
                  Join thousands of pet families who trust our digital identity platform for comprehensive pet care management.
                </p>
              </div>
              
              <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
                <div className="text-center">
                  <div className="text-2xl lg:text-3xl font-bold bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent mb-2">$847</div>
                  <div className="text-lg font-semibold text-green-300 mb-1">Average Annual Savings</div>
                  <div className="text-blue-200 text-sm">Through preventive care and faster emergency response</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl lg:text-3xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent mb-2">45 min</div>
                  <div className="text-lg font-semibold text-blue-300 mb-1">Time Saved Per Visit</div>
                  <div className="text-blue-200 text-sm">With instant access to complete medical records</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl lg:text-3xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent mb-2">99.8%</div>
                  <div className="text-lg font-semibold text-purple-300 mb-1">Uptime Reliability</div>
                  <div className="text-blue-200 text-sm">Your pet's identity is always accessible when needed</div>
                </div>
              </div>
            </div>
          </div>
        </FadeIn>
      </div>
    </section>
  );
};

export default FeatureDetailSection;
